import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select, Spin } from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
} from '@ant-design/icons';
import { useSearchQuery } from '@/state/slices/search';
import { MIN_QUERY_LENGTH } from '@/utils/search';
import { SubHeader } from '@/features/sub-header';
import { PatientRow, StudyRow, ReportRow } from '@/features/search-results';
import styles from './SearchResults.module.css';

type CategoryFilter = 'all' | 'patients' | 'studies' | 'reports';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';
  const [filter, setFilter] = useState<CategoryFilter>('all');

  const { data, isFetching } = useSearchQuery(queryParam, {
    skip: queryParam.length < MIN_QUERY_LENGTH,
  });

  useEffect(() => {
    setFilter('all');
  }, [queryParam]);

  const filteredData = useMemo(() => {
    if (!data) return { patients: [], studies: [], reports: [] };
    return {
      patients: filter === 'all' || filter === 'patients' ? data.patients : [],
      studies: filter === 'all' || filter === 'studies' ? data.studies : [],
      reports: filter === 'all' || filter === 'reports' ? data.reports : [],
    };
  }, [data, filter]);

  const totalFiltered =
    filteredData.patients.length +
    filteredData.studies.length +
    filteredData.reports.length;

  if (queryParam.length < MIN_QUERY_LENGTH) {
    return (
      <div className={styles.page}>
        <SubHeader title="Search" />
        <div className={styles.centered}>
          <SearchOutlined style={{ fontSize: 32, opacity: 0.3 }} />
          <span>Enter at least {MIN_QUERY_LENGTH} characters to search</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <SubHeader title="Search Results" />

      <div className={styles.toolbar}>
        <span className={styles.queryLabel}>
          Results for <strong>&ldquo;{queryParam}&rdquo;</strong>
        </span>

        <Select
          className={styles.filterSelect}
          value={filter}
          onChange={(v) => setFilter(v)}
          options={[
            { value: 'all', label: 'All Categories' },
            { value: 'patients', label: 'Patients' },
            { value: 'studies', label: 'Studies' },
            { value: 'reports', label: 'Reports' },
          ]}
          size="small"
        />

        {!isFetching && data && (
          <span className={styles.countLabel}>
            {totalFiltered} result{totalFiltered !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {isFetching && (
        <div className={styles.centered}>
          <Spin size="large" />
          <span>Searching...</span>
        </div>
      )}
      {!isFetching && totalFiltered === 0 && (
        <div className={styles.centered}>
          <SearchOutlined style={{ fontSize: 32, opacity: 0.3 }} />
          <span>No results found for &ldquo;{queryParam}&rdquo;</span>
        </div>
      )}
      {!isFetching && totalFiltered > 0 && (
        <div className={styles.resultsList}>
          {filteredData.patients.length > 0 && (
            <div className={styles.sectionGroup}>
              <div className={styles.sectionTitle}>
                <UserOutlined className={styles.sectionTitleIcon} />
                Patients
                <span className={styles.sectionCount}>
                  ({filteredData.patients.length})
                </span>
              </div>
              {filteredData.patients.map((patient) => (
                <PatientRow
                  key={patient.uuid}
                  patient={patient}
                  query={queryParam}
                />
              ))}
            </div>
          )}

          {filteredData.studies.length > 0 && (
            <div className={styles.sectionGroup}>
              <div className={styles.sectionTitle}>
                <FolderOpenOutlined className={styles.sectionTitleIcon} />
                Studies
                <span className={styles.sectionCount}>
                  ({filteredData.studies.length})
                </span>
              </div>
              {filteredData.studies.map((study) => (
                <StudyRow key={study.uuid} study={study} query={queryParam} />
              ))}
            </div>
          )}

          {filteredData.reports.length > 0 && (
            <div className={styles.sectionGroup}>
              <div className={styles.sectionTitle}>
                <FileTextOutlined className={styles.sectionTitleIcon} />
                Reports
                <span className={styles.sectionCount}>
                  ({filteredData.reports.length})
                </span>
              </div>
              {filteredData.reports.map((report) => (
                <ReportRow
                  key={report.uuid}
                  report={report}
                  query={queryParam}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchResults;
